import axiosInstance from '../config/axios.config';
import { shouldBlockFeature } from '../utils/profileUtils';

// Cache for report services to prevent duplicate API calls
let reportServicesCache = null;
let reportServicesCacheTime = null;
let pendingFetchRequest = null; // Track ongoing request to prevent duplicates
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Build Lead Marketplace menu section with Dashboard, Receive/Refer Leads, Commission Payouts, Lead Deals, and Wallet
const buildLeadFlowSection = () => {
    return {
        label: 'Lead Marketplace',
        icon: 'pi pi-fw pi-share-alt',
        items: [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-th-large',
                to: '/app/marketplace-dashboard'
            },
            {
                label: 'Receive Or Refer Leads',
                icon: 'pi pi-fw pi-shopping-bag',
                items: [
                    {
                        label: 'Receive Leads',
                        icon: 'pi pi-fw pi-shopping-cart',
                        to: '/app/marketplace'
                    },
                    {
                        label: 'Refer Leads',
                        icon: 'pi pi-fw pi-tag',
                        to: '/app/marketplace/my-listings'
                    }
                ]
            },
            {
                label: 'Commission Payouts',
                icon: 'pi pi-fw pi-percentage',
                items: [
                    {
                        label: 'Market Payout',
                        icon: 'pi pi-fw pi-shopping-cart',
                        to: '/app/dsa-offers'
                    },
                    {
                        label: 'My Payouts',
                        icon: 'pi pi-fw pi-wallet',
                        to: '/app/dsa-offers/my-offers'
                    }
                ]
            },
            {
                label: 'Lead Deals',
                icon: 'pi pi-fw pi-briefcase',
                to: '/app/collaborations'
            },
            {
                label: 'Commission Wallet',
                icon: 'pi pi-fw pi-wallet',
                to: '/app/leadflow/wallet'
            }
        ]
    };
};

// "Credits and Subscription Management" — one parent grouping the money screens:
// Subscription (plans), Credits (wallet + recharge), Transaction History.
// Used by every role builder so the grouping is identical everywhere.
const buildCreditsSubscriptionSection = (isCreditsLocked) => {
    return {
        label: 'Credits and Subscription Management',
        icon: 'pi pi-fw pi-credit-card',
        items: [
            { label: 'Subscription', icon: 'pi pi-fw pi-tags', to: '/app/subscription/plans' },
            {
                label: isCreditsLocked ? 'Credits 🔒' : 'Credits',
                icon: isCreditsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-wallet',
                to: '/app/credit-wallet',
                className: isCreditsLocked ? 'p-disabled opacity-60' : '',
                locked: isCreditsLocked,
            },
            { label: 'Transaction History', icon: 'pi pi-fw pi-history', to: '/app/transaction-history' },
        ],
    };
};

// Simplified menu service with caching to prevent duplicate API calls
export const menuService = {
    // Simple API fetch for reports with caching
    fetchReportServices: async (forceRefresh = false) => {
        try {
            // If there's already a pending request, return it
            if (pendingFetchRequest && !forceRefresh) {
                console.log('⚠️  Request already in progress, returning existing promise - No duplicate API call');
                return pendingFetchRequest;
            }

            // Check if cache is valid and not forcing refresh
            const now = Date.now();
            if (!forceRefresh && reportServicesCache && reportServicesCacheTime) {
                const cacheAge = now - reportServicesCacheTime;
                if (cacheAge < CACHE_DURATION) {
                    console.log('✅ Using cached report services (age: ' + Math.round(cacheAge / 1000) + 's) - No API call');
                    return reportServicesCache;
                }
            }

            console.log('🌐 Fetching report services from API...');

            // Create and store the promise
            pendingFetchRequest = axiosInstance.get('/user/report-services/categorized');
            const response = await pendingFetchRequest;

            console.log('Report services response:', response);

            if (response.data && response.data.success && response.data.data) {
                const data = response.data.data;
                /* Only cache non-empty results. A transient empty response
                   (backend hiccup, missing tenant data, partial reply) used
                   to get cached for 5 minutes and made the entire Borrower
                   Reports menu collapse to just "Report History" until the
                   cache expired. Now we skip the cache write on empty so
                   the next call will retry. */
                const hasContent = Array.isArray(data)
                    ? data.length > 0
                    : (typeof data === 'object' && Object.keys(data).length > 0);

                if (hasContent) {
                    reportServicesCache = data;
                    reportServicesCacheTime = now;
                    console.log('✅ Report services cached successfully');
                    console.log('📊 DATA SHAPE:', {
                        type: Array.isArray(data) ? 'array' : typeof data,
                        length: Array.isArray(data) ? data.length : Object.keys(data).length,
                        sample: JSON.stringify(data).slice(0, 500)
                    });
                } else {
                    console.warn('⚠️ Report services API returned an empty payload — skipping cache write so the next call retries.');
                }

                // ✅ FIX: Clear the pending request after successful completion
                pendingFetchRequest = null;

                return data;
            }

            console.warn('Report services API returned unexpected structure:', response.data);

            // ✅ FIX: Clear pending request even if response structure is unexpected
            pendingFetchRequest = null;

            return [];
        } catch (error) {
            console.error('Failed to fetch report services:', error);

            // ✅ FIX: Clear the pending request on error so retries can work
            pendingFetchRequest = null;

            // Return cached data if available, even if expired
            if (reportServicesCache) {
                console.log('⚠️ API failed, using stale cache');
                return reportServicesCache;
            }
            return [];
        }
    },

    // Clear cache (useful after credits purchase or service updates)
    clearCache: () => {
        console.log('🗑️ Clearing report services cache');
        reportServicesCache = null;
        reportServicesCacheTime = null;
    },

    // Build complete menu structure based on user roles
    getMenuItems: async (setSelectedLoanUrl, profileCompletion) => {
        const categorizedServices = await menuService.fetchReportServices();

        // Build reports section with API data
        const reportsSection = buildReportsSection(categorizedServices, profileCompletion);

        const leadFlowSection = buildLeadFlowSection();

        // Check if features should be locked
        const isCreditsLocked = profileCompletion && shouldBlockFeature('credits', profileCompletion);
        const isSharedLeadsLocked = profileCompletion && shouldBlockFeature('shared-leads', profileCompletion);

        // Get user roles from localStorage
        const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        // Determine primary role (first role or fallback to user_type)
        const primaryRole = userRoles.length > 0 ? userRoles[0] : (user.user_type || 'user');

        // Get role-specific menu structure
        return menuService.getRoleBasedMenus(primaryRole, reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked);
    },

    // Get menu items by role (called from AppMenu.jsx)
    getMenuItemsByRole: async (role, setSelectedLoanUrl, forceRefresh = false) => {
        try {
            const categorizedServices = await menuService.fetchReportServices(forceRefresh);

            // Build reports section with API data
            const reportsSection = buildReportsSection(categorizedServices, null);

            const leadFlowSection = buildLeadFlowSection();

            // Get role-specific menu structure
            return menuService.getRoleBasedMenus(role, reportsSection, leadFlowSection, setSelectedLoanUrl, false, false);
        } catch (error) {
            console.error('Error in getMenuItemsByRole:', error);
            // Return fallback menu
            return [
                {
                    label: '',
                    items: [
                        { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/app/dashboard' },
                        { separator: true },
                        buildCreditsSubscriptionSection(false)
                    ]
                }
            ];
        }
    },

    // Define role-based menu structures
    getRoleBasedMenus: (role, reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        const roleMenus = {
            tenant_admin: menuService.getAdminMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked),
            manager: menuService.getManagerMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked),
            user: menuService.getUserMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked),
            owner: menuService.getAdminMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked), // Owner same as admin
            company_owner: menuService.getCompanyAdminMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked),
            company_admin: menuService.getCompanyAdminMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked),
            company_user: menuService.getCompanyUserMenus(reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked)
        };

        const menu = roleMenus[role] || roleMenus.user; // Default to user menu if role not found

        // Inject broadcast menu items for any user with a company_id
        // (regardless of user_type — anyone in a company should see these)
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.company_id && !['company_owner', 'company_admin', 'company_user'].includes(role)) {
            const broadcastItems = [
                { label: 'Available Offers', icon: 'pi pi-fw pi-megaphone', to: '/app/leads/available-offers' },
                { label: 'Claims', icon: 'pi pi-fw pi-inbox', to: '/app/leads/received-claims' }
            ];
            // Find the Leads section and append broadcast items
            menu.forEach(section => {
                if (section.items) {
                    section.items.forEach(item => {
                        if (item.label === 'Leads Management' && item.items) {
                            // Only add if not already present
                            if (!item.items.some(i => i.to === '/app/leads/available-offers')) {
                                item.items.push(...broadcastItems);
                            }
                        }
                    });
                }
            });
        }

        // Filter out null items and consecutive separators (e.g. when leadFlowSection is hidden)
        return menu.map(section => ({
            ...section,
            items: section.items
                ?.filter(item => item != null)
                .filter((item, idx, arr) => !(item.separator && arr[idx - 1]?.separator))
        }));
    },

    // Admin menu structure - New order: Dashboard, Leads, Reports, Marketplace, Admin, Agent Network, Credits
    getAdminMenus: (reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        return [
            {
                label: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        to: '/app/dashboard'
                    },
                    { separator: true },
                    // Lending Network - opens For Business page
                    {
                        label: 'Lending Network',
                        icon: 'pi pi-fw pi-globe',
                        to: '/app/business/pages'
                    },
                    { separator: true },
                    {
                        label: 'Leads Management',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'My Leads Dashboard',
                                icon: 'pi pi-fw pi-chart-bar',
                                to: '/app/my-dashboard'
                            },
                            {
                                label: 'View Leads',
                                icon: 'pi pi-fw pi-list',
                                to: '/app/leads'
                            },
                            {
                                label: 'Create Lead',
                                icon: 'pi pi-fw pi-plus',
                                to: '/app/leads/create'
                            },
                            {
                                label: isSharedLeadsLocked ? 'Shared Leads 🔒' : 'Shared Leads',
                                icon: isSharedLeadsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-share-alt',
                                to: '/app/leads/shared',
                                className: isSharedLeadsLocked ? 'p-disabled opacity-60' : '',
                                locked: isSharedLeadsLocked
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'Borrower Reports',
                        icon: 'pi pi-fw pi-file',
                        items: reportsSection.items
                    },
                    { separator: true },
                    // LeadFlow Section (Marketplace + Collaborations)
                    leadFlowSection,
                    { separator: true },
                    // Admin section (always visible for admin)
                    {
                        label: 'Admin',
                        icon: 'pi pi-fw pi-shield',
                        items: [
                            {
                                label: 'User Management',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/admin/users'
                            },
                            {
                                label: 'User Role Management',
                                icon: 'pi pi-fw pi-user-edit',
                                to: '/app/admin/user-management'
                            },
                            {
                                label: 'Invoice Management',
                                icon: 'pi pi-fw pi-file-pdf',
                                to: '/app/admin/invoice-management'
                            },
                            {
                                label: 'Credit Management',
                                icon: 'pi pi-fw pi-wallet',
                                to: '/app/admin/credit-management'
                            },
                            {
                                label: 'Company Sharing',
                                icon: 'pi pi-fw pi-share-alt',
                                to: '/app/admin/company-sharing'
                            },
                            {
                                label: 'Lending Network Access',
                                icon: 'pi pi-fw pi-shield',
                                to: '/app/admin/lending-network'
                            },
                            {
                                label: 'Storage Usage',
                                icon: 'pi pi-fw pi-database',
                                to: '/app/admin/storage'
                            },
                            {
                                label: 'Notification Settings',
                                icon: 'pi pi-fw pi-bell',
                                to: '/app/admin/notification-settings'
                            }
                        ]
                    },
                    { separator: true },
                    // Agent Network section (MLM only for tenant admin)
                    {
                        label: 'Agent Network',
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            {
                                label: 'MLM Network',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/tree/mlm-network'
                            }
                        ]
                    },
                    { separator: true },
                    buildCreditsSubscriptionSection(isCreditsLocked),
                    { separator: true },
                    // Support tickets — tenant_admin sees both My Issues + Inbox
                    {
                        label: 'Support',
                        icon: 'pi pi-fw pi-comments',
                        items: [
                            { label: 'My Issues', icon: 'pi pi-fw pi-list',  to: '/app/support/my-issues' },
                            { label: 'Inbox',     icon: 'pi pi-fw pi-inbox', to: '/app/support/inbox' }
                        ]
                    },
                    { separator: true },
                    // Prototype (Demo) — tenant_admin only; removed after prototype promotion
                    {
                        label: 'Prototype (Demo)',
                        icon: 'pi pi-fw pi-bolt',
                        items: [
                            { label: 'Overview',      icon: 'pi pi-fw pi-th-large', to: '/app/prototype/social' },
                            { label: 'My Profile',    icon: 'pi pi-fw pi-user',     to: '/app/prototype/social/profile' },
                            { label: 'Edit Profile',  icon: 'pi pi-fw pi-pencil',   to: '/app/prototype/social/edit-profile' },
                            { label: 'My Network',    icon: 'pi pi-fw pi-users',    to: '/app/prototype/social/network' },
                            { label: 'Feed',          icon: 'pi pi-fw pi-comments', to: '/app/prototype/social/feed' },
                            { label: 'Messages',      icon: 'pi pi-fw pi-send',     to: '/app/prototype/social/messages' },
                            { label: 'Notifications', icon: 'pi pi-fw pi-bell',     to: '/app/prototype/social/notifications' },
                            { label: 'MLM Network v2',  icon: 'pi pi-fw pi-sitemap', to: '/app/prototype/network/mlm' },
                        ]
                    }
                ]
            }
        ];
    },

    // Manager menu structure - New order: Dashboard, Leads, Reports, Marketplace, Agent Network, Credits
    getManagerMenus: (reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        return [
            {
                label: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        to: '/app/dashboard'
                    },
                    { separator: true },
                    // Lending Network - opens For Business page
                    {
                        label: 'Lending Network',
                        icon: 'pi pi-fw pi-globe',
                        to: '/app/business/pages'
                    },
                    { separator: true },
                    {
                        label: 'Leads Management',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'My Leads Dashboard',
                                icon: 'pi pi-fw pi-chart-bar',
                                to: '/app/my-dashboard'
                            },
                            {
                                label: 'View Leads',
                                icon: 'pi pi-fw pi-list',
                                to: '/app/leads'
                            },
                            {
                                label: 'Create Lead',
                                icon: 'pi pi-fw pi-plus',
                                to: '/app/leads/create'
                            },
                            {
                                label: isSharedLeadsLocked ? 'Shared Leads 🔒' : 'Shared Leads',
                                icon: isSharedLeadsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-share-alt',
                                to: '/app/leads/shared',
                                className: isSharedLeadsLocked ? 'p-disabled opacity-60' : '',
                                locked: isSharedLeadsLocked
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'Borrower Reports',
                        icon: 'pi pi-fw pi-file',
                        items: reportsSection.items
                    },
                    { separator: true },
                    // LeadFlow Section (Marketplace + Collaborations)
                    leadFlowSection,
                    { separator: true },
                    // Agent Network section (MLM only for manager)
                    {
                        label: 'Agent Network',
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            {
                                label: 'MLM Network',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/tree/mlm-network'
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'My Storage',
                        icon: 'pi pi-fw pi-database',
                        to: '/app/storage/my'
                    },
                    { separator: true },
                    buildCreditsSubscriptionSection(isCreditsLocked),
                    { separator: true },
                    // Support tickets — reporter "My Issues" only.
                    // (company_admin builder below upgrades this to include Inbox.)
                    {
                        label: 'Support',
                        icon: 'pi pi-fw pi-comments',
                        items: [
                            { label: 'My Issues', icon: 'pi pi-fw pi-list', to: '/app/support/my-issues' }
                        ]
                    }
                ]
            }
        ];
    },

    // User menu structure - New order: Dashboard, Leads, Reports, Marketplace, Agent Network, Credits
    getUserMenus: (reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        return [
            {
                label: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        to: '/app/dashboard'
                    },
                    { separator: true },
                    // Lending Network - opens For Business page
                    {
                        label: 'Lending Network',
                        icon: 'pi pi-fw pi-globe',
                        to: '/app/business/pages'
                    },
                    { separator: true },
                    {
                        label: 'Leads Management',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'My Leads Dashboard',
                                icon: 'pi pi-fw pi-chart-bar',
                                to: '/app/my-dashboard'
                            },
                            {
                                label: 'View Leads',
                                icon: 'pi pi-fw pi-list',
                                to: '/app/leads'
                            },
                            {
                                label: 'Create Lead',
                                icon: 'pi pi-fw pi-plus',
                                to: '/app/leads/create'
                            },
                            {
                                label: isSharedLeadsLocked ? 'Shared Leads 🔒' : 'Shared Leads',
                                icon: isSharedLeadsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-share-alt',
                                to: '/app/leads/shared',
                                className: isSharedLeadsLocked ? 'p-disabled opacity-60' : '',
                                locked: isSharedLeadsLocked
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'Borrower Reports',
                        icon: 'pi pi-fw pi-file',
                        items: reportsSection.items
                    },
                    { separator: true },
                    // LeadFlow Section (Marketplace + Collaborations)
                    leadFlowSection,
                    { separator: true },
                    // Agent Network section (MLM only - no Org Chart for regular users)
                    {
                        label: 'Agent Network',
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            {
                                label: 'MLM Network',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/tree/mlm-network'
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'My Storage',
                        icon: 'pi pi-fw pi-database',
                        to: '/app/storage/my'
                    },
                    { separator: true },
                    buildCreditsSubscriptionSection(isCreditsLocked),
                    { separator: true },
                    // Support tickets — reporter "My Issues" only.
                    // (company_admin builder below upgrades this to include Inbox.)
                    {
                        label: 'Support',
                        icon: 'pi pi-fw pi-comments',
                        items: [
                            { label: 'My Issues', icon: 'pi pi-fw pi-list', to: '/app/support/my-issues' }
                        ]
                    }
                ]
            }
        ];
    },

    // Company Admin menu structure - New order: Dashboard, Leads, Reports, Marketplace, Company Admin, Agent Network, Credits
    getCompanyAdminMenus: (reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        return [
            {
                label: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        to: '/app/dashboard'
                    },
                    { separator: true },
                    // Lending Network - opens For Business page
                    {
                        label: 'Lending Network',
                        icon: 'pi pi-fw pi-globe',
                        to: '/app/business/pages'
                    },
                    { separator: true },
                    {
                        label: 'Leads Management',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'Leads Dashboard',
                                icon: 'pi pi-fw pi-chart-bar',
                                to: '/app/company/leads-dashboard'
                            },
                            {
                                label: 'View Leads',
                                icon: 'pi pi-fw pi-list',
                                to: '/app/leads'
                            },
                            {
                                label: 'Create Lead',
                                icon: 'pi pi-fw pi-plus',
                                to: '/app/leads/create'
                            },
                            {
                                label: isSharedLeadsLocked ? 'Shared Leads 🔒' : 'Shared Leads',
                                icon: isSharedLeadsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-share-alt',
                                to: '/app/leads/shared',
                                className: isSharedLeadsLocked ? 'p-disabled opacity-60' : '',
                                locked: isSharedLeadsLocked
                            },
                            {
                                label: 'Available Offers',
                                icon: 'pi pi-fw pi-megaphone',
                                to: '/app/leads/available-offers'
                            },
                            {
                                label: 'Claims',
                                icon: 'pi pi-fw pi-inbox',
                                to: '/app/leads/received-claims'
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'Borrower Reports',
                        icon: 'pi pi-fw pi-file',
                        items: reportsSection.items
                    },
                    { separator: true },
                    // LeadFlow Section (Marketplace + Collaborations)
                    leadFlowSection,
                    { separator: true },
                    // Company Admin section (only for company admins)
                    {
                        label: 'Company Admin',
                        icon: 'pi pi-fw pi-building',
                        items: [
                            {
                                label: 'Manage Users',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/company/users'
                            },
                            {
                                label: 'Manage Agents',
                                icon: 'pi pi-fw pi-id-card',
                                to: '/app/company/agents'
                            },
                            {
                                label: 'User Role Management',
                                icon: 'pi pi-fw pi-user-edit',
                                to: '/app/company/role-management'
                            },
                            {
                                label: 'Invoices',
                                icon: 'pi pi-fw pi-file-pdf',
                                to: '/app/company/invoices'
                            },
                            {
                                label: 'Credit Management',
                                icon: 'pi pi-fw pi-wallet',
                                to: '/app/company/credits'
                            },
                            {
                                label: 'Storage Usage',
                                icon: 'pi pi-fw pi-database',
                                to: '/app/storage/company'
                            },
                            {
                                label: 'Lending Network Access',
                                icon: 'pi pi-fw pi-shield',
                                to: '/app/company/lending-network'
                            }
                        ]
                    },
                    { separator: true },
                    // Agent Network section
                    {
                        label: 'Agent Network',
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            {
                                label: 'Org Chart',
                                icon: 'pi pi-fw pi-building',
                                to: '/app/tree/org-chart'
                            },
                            {
                                label: 'MLM Network',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/tree/mlm-network'
                            }
                        ]
                    },
                    { separator: true },
                    buildCreditsSubscriptionSection(isCreditsLocked),
                    { separator: true },
                    // Support tickets — company_admin gets My Issues + Inbox.
                    {
                        label: 'Support',
                        icon: 'pi pi-fw pi-comments',
                        items: [
                            { label: 'My Issues', icon: 'pi pi-fw pi-list',  to: '/app/support/my-issues' },
                            { label: 'Inbox',     icon: 'pi pi-fw pi-inbox', to: '/app/support/inbox' }
                        ]
                    }
                ]
            }
        ];
    },

    // Company User menu structure - New order: Dashboard, Leads, Reports, Marketplace, Agent Network, Credits
    getCompanyUserMenus: (reportsSection, leadFlowSection, setSelectedLoanUrl, isCreditsLocked, isSharedLeadsLocked) => {
        return [
            {
                label: '',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'pi pi-fw pi-home',
                        to: '/app/dashboard'
                    },
                    { separator: true },
                    // Lending Network - opens For Business page
                    {
                        label: 'Lending Network',
                        icon: 'pi pi-fw pi-globe',
                        to: '/app/business/pages'
                    },
                    { separator: true },
                    {
                        label: 'Leads Management',
                        icon: 'pi pi-fw pi-users',
                        items: [
                            {
                                label: 'My Leads Dashboard',
                                icon: 'pi pi-fw pi-chart-bar',
                                to: '/app/my-dashboard'
                            },
                            {
                                label: 'View Leads',
                                icon: 'pi pi-fw pi-list',
                                to: '/app/leads'
                            },
                            {
                                label: 'Create Lead',
                                icon: 'pi pi-fw pi-plus',
                                to: '/app/leads/create'
                            },
                            {
                                label: isSharedLeadsLocked ? 'Shared Leads 🔒' : 'Shared Leads',
                                icon: isSharedLeadsLocked ? 'pi pi-fw pi-lock' : 'pi pi-fw pi-share-alt',
                                to: '/app/leads/shared',
                                className: isSharedLeadsLocked ? 'p-disabled opacity-60' : '',
                                locked: isSharedLeadsLocked
                            },
                            {
                                label: 'Available Offers',
                                icon: 'pi pi-fw pi-megaphone',
                                to: '/app/leads/available-offers'
                            },
                            {
                                label: 'Claims',
                                icon: 'pi pi-fw pi-inbox',
                                to: '/app/leads/received-claims'
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'Borrower Reports',
                        icon: 'pi pi-fw pi-file',
                        items: reportsSection.items
                    },
                    { separator: true },
                    // LeadFlow Section (Marketplace + Collaborations)
                    leadFlowSection,
                    { separator: true },
                    // Agent Network section with Org Chart (company employees can see their company org chart)
                    {
                        label: 'Agent Network',
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            {
                                label: 'Org Chart',
                                icon: 'pi pi-fw pi-building',
                                to: '/app/tree/org-chart'
                            },
                            {
                                label: 'MLM Network',
                                icon: 'pi pi-fw pi-users',
                                to: '/app/tree/mlm-network'
                            }
                        ]
                    },
                    { separator: true },
                    {
                        label: 'My Storage',
                        icon: 'pi pi-fw pi-database',
                        to: '/app/storage/my'
                    },
                    { separator: true },
                    buildCreditsSubscriptionSection(isCreditsLocked),
                    { separator: true },
                    // Support tickets — reporter "My Issues" only.
                    // (company_admin builder below upgrades this to include Inbox.)
                    {
                        label: 'Support',
                        icon: 'pi pi-fw pi-comments',
                        items: [
                            { label: 'My Issues', icon: 'pi pi-fw pi-list', to: '/app/support/my-issues' }
                        ]
                    }
                ]
            }
        ];
    }
};

// Build reports section with API categories
const buildReportsSection = (categorizedServices, profileCompletion) => {
    let categoryItems = [];

    console.log('🔍 buildReportsSection input:', {
        hasData: !!categorizedServices,
        isObject: typeof categorizedServices === 'object',
        isArray: Array.isArray(categorizedServices),
        keyCount: categorizedServices ? Object.keys(categorizedServices).length : 0,
        firstKey: categorizedServices ? Object.keys(categorizedServices)[0] : null,
        firstValue: categorizedServices ? JSON.stringify(Object.values(categorizedServices)[0]).slice(0, 300) : null
    });

    // Handle your specific API response format with numeric keys
    if (categorizedServices && typeof categorizedServices === 'object') {
        // Convert object with numeric keys to array
        const categoriesArray = Object.values(categorizedServices);
        console.log('🔍 categoriesArray length:', categoriesArray.length);

        categoryItems = categoriesArray
            .filter(category => {
                const pass = category && category.services && Array.isArray(category.services) && category.services.length > 0;
                if (!pass) {
                    console.log('❌ Category filtered out:', { name: category?.name, hasServices: !!category?.services, isArray: Array.isArray(category?.services), length: category?.services?.length, servicesType: typeof category?.services });
                }
                return pass;
            })
            .map(category => {
                const catLower = (category.name || '').toLowerCase();
                const isDigilocker = catLower.includes('digilocker');
                const isEsign = catLower.includes('esign') || catLower.includes('e-sign') || catLower.includes('e sign') || catLower.includes('signature');
                const isFace = catLower.includes('face') || catLower.includes('liveness');

                // For DigiLocker, E-Sign, and Face categories: collapse all individual
                // steps into a single guided-flow entry so users don't need to
                // copy-paste the client_id / session token between separate pages.
                if (isDigilocker || isEsign || isFace) {
                    // Pick the "initialize" service ID (first step of the flow) so the
                    // flow component can fetch its credit cost and deduct correctly.
                    let initService;
                    if (isDigilocker) {
                        initService = category.services.find(s => s.name.toLowerCase().includes('initialize'))
                            || category.services[0];
                    } else if (isEsign) {
                        // Use the base initialize (not NSDL / SureSign / HSM variants)
                        initService = category.services.find(s => {
                            const n = s.name.toLowerCase();
                            return n.includes('initialize') && !n.includes('nsdl') && !n.includes('suresign') && !n.includes('hsm');
                        }) || category.services.find(s => s.name.toLowerCase().includes('initialize'))
                          || category.services[0];
                    } else {
                        // Face: use liveness-v4 or first liveness service
                        initService = category.services.find(s => {
                            const n = s.name.toLowerCase();
                            return n.includes('liveness') && n.includes('v4');
                        }) || category.services.find(s => s.name.toLowerCase().includes('liveness'))
                          || category.services[0];
                    }

                    const featureKey = isDigilocker ? 'digilocker' : isEsign ? 'esign' : 'face';
                    const isLocked = profileCompletion && shouldBlockFeature(featureKey, profileCompletion);

                    let flowLabel, flowIcon, flowRoute;
                    if (isDigilocker) {
                        flowLabel = 'DigiLocker Flow';
                        flowIcon = 'pi pi-fw pi-folder-open';
                        flowRoute = `/app/reports/surepass-digilocker-flow/${initService.id}`;
                    } else if (isEsign) {
                        flowLabel = 'E-Sign Flow';
                        flowIcon = 'pi pi-fw pi-pencil';
                        flowRoute = `/app/reports/surepass-esign-flow/${initService.id}`;
                    } else {
                        flowLabel = 'Face Verification Flow';
                        flowIcon = 'pi pi-fw pi-eye';
                        flowRoute = `/app/reports/surepass-face-flow/${initService.id}`;
                    }

                    return {
                        label: category.name,
                        icon: getSimpleIcon(category.name),
                        items: [{
                            label: isLocked ? `${flowLabel} 🔒` : flowLabel,
                            icon: isLocked ? 'pi pi-fw pi-lock' : flowIcon,
                            to: flowRoute,
                            serviceId: initService.id,
                            className: isLocked ? 'p-disabled opacity-60' : '',
                            locked: isLocked,
                        }],
                    };
                }

                // All other categories: check if any MCA flow services exist and group them
                const mcaCreate = category.services.find(s => s.name.toLowerCase().includes('mca') && s.name.toLowerCase().includes('create'));
                const mcaList   = category.services.find(s => s.name.toLowerCase().includes('mca') && s.name.toLowerCase().includes('list'));
                const mcaStatus = category.services.find(s => s.name.toLowerCase().includes('mca') && (s.name.toLowerCase().includes('status') || s.name.toLowerCase().includes('get status')));
                const hasMcaFlow = mcaCreate && mcaList && mcaStatus;

                // Check if Udyam OTP Send + Submit exist and group them
                const udyamOtpSend   = category.services.find(s => s.name.toLowerCase().includes('udyam') && s.name.toLowerCase().includes('otp') && s.name.toLowerCase().includes('send'));
                const udyamOtpSubmit = category.services.find(s => s.name.toLowerCase().includes('udyam') && s.name.toLowerCase().includes('otp') && (s.name.toLowerCase().includes('submit') || s.name.toLowerCase().includes('verify')));
                const hasUdyamOtpFlow = udyamOtpSend && udyamOtpSubmit;

                // Check if ITR services exist and collapse into a single flow
                const itrCreateSvc = category.services.find(s => s.name.toLowerCase().includes('itr') && s.name.toLowerCase().includes('create'));
                const hasItrFlow = !!itrCreateSvc && category.services.some(s => s.name.toLowerCase().startsWith('itr'));

                // Check if SE State List + SE Details exist and group into a flow
                const seStateListSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return (n.includes('se') || n.includes('shop') || n.includes('establishment')) && n.includes('state');
                });
                const seDetailsSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return (n.includes('se') || n.includes('shop') || n.includes('establishment')) && n.includes('detail');
                });
                const hasSeFlow = !!(seStateListSvc && seDetailsSvc);

                // Check if Fuel Price State List + Fuel Price exist and group into a flow
                const fuelStateListSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return n.includes('fuel') && n.includes('state');
                });
                const fuelPriceSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return n.includes('fuel') && !n.includes('state');
                });
                const hasFuelFlow = !!(fuelStateListSvc && fuelPriceSvc);

                // Check if Electricity Operator List + Electricity Bill exist and group into a flow
                const electricityOperatorListSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return n.includes('electricity') && (n.includes('operator') || n.includes('list'));
                });
                const electricityBillSvc = category.services.find(s => {
                    const n = s.name.toLowerCase();
                    return n.includes('electricity') && n.includes('bill');
                });
                const hasElectricityFlow = !!(electricityOperatorListSvc && electricityBillSvc);

                const serviceItems = category.services
                    .filter(service => {
                        // Hide "Prefill Report" — only "Prefill By Mobile" is shown
                        const n = service.name.toLowerCase();
                        if (n.includes('prefill') && !n.includes('mobile')) return false;
                        // Remove individual MCA steps if we have a complete flow
                        if (hasMcaFlow) {
                            const n = service.name.toLowerCase();
                            const isMcaStep = n.includes('mca') && (
                                n.includes('create') || n.includes('list') || n.includes('status')
                            );
                            if (isMcaStep) return false;
                        }
                        // Remove individual Udyam OTP steps if we have a complete flow
                        if (hasUdyamOtpFlow) {
                            const n = service.name.toLowerCase();
                            if (n.includes('udyam') && n.includes('otp')) return false;
                        }
                        // Remove the individual Surepass ITR steps once the combined
                        // "ITR Verification Flow" entry exists.
                        //
                        // Matched on navigation_url, NOT the name. The old rule hid
                        // ANY service whose name starts with "itr", which silently
                        // swallowed the unrelated GLIB "ITR Fetch" (consent journey)
                        // — it never appeared in the sidebar at all. Only the
                        // Surepass steps live under /surepass-itr-*, so keying on
                        // that collapses exactly the intended entries.
                        if (hasItrFlow
                            && service.name.toLowerCase().startsWith('itr')
                            && String(service.navigation_url || '').includes('surepass-itr')) {
                            return false;
                        }
                        // Remove individual SE state list and SE details if we have a flow
                        if (hasSeFlow && (service.id === seStateListSvc.id || service.id === seDetailsSvc.id)) return false;
                        // Remove individual fuel state list and fuel price if we have a flow
                        if (hasFuelFlow && (service.id === fuelStateListSvc.id || service.id === fuelPriceSvc.id)) return false;
                        // Remove individual electricity operator list and electricity bill if we have a flow
                        if (hasElectricityFlow && (service.id === electricityOperatorListSvc.id || service.id === electricityBillSvc.id)) return false;
                        return true;
                    })
                    .map(service => {
                        const featureKey = getFeatureKeyFromService(service.name);
                        const isLocked = profileCompletion && shouldBlockFeature(featureKey, profileCompletion);
                        return {
                            label: isLocked ? `${service.name} 🔒` : service.name,
                            icon: isLocked ? 'pi pi-fw pi-lock' : getSimpleIcon(service.name),
                            to: buildSimpleRoute(service),
                            serviceId: service.id,
                            className: isLocked ? 'p-disabled opacity-60' : '',
                            locked: isLocked,
                        };
                    });

                if (hasMcaFlow) {
                    const mcaFlowRoute = `/app/reports/surepass-mca-flow/${mcaCreate.id}/${mcaList.id}/${mcaStatus.id}`;
                    serviceItems.unshift({
                        label: 'MCA Documents Flow',
                        icon: 'pi pi-fw pi-briefcase',
                        to: mcaFlowRoute,
                        serviceId: mcaCreate.id,
                    });
                }

                if (hasUdyamOtpFlow) {
                    const udyamOtpFlowRoute = `/app/reports/surepass-udyam-otp-flow/${udyamOtpSend.id}/${udyamOtpSubmit.id}`;
                    serviceItems.unshift({
                        label: 'Udyam OTP Verification',
                        icon: 'pi pi-fw pi-send',
                        to: udyamOtpFlowRoute,
                        serviceId: udyamOtpSend.id,
                    });
                }

                if (hasItrFlow) {
                    serviceItems.push({
                        label: 'ITR Verification Flow',
                        icon: 'pi pi-fw pi-file-edit',
                        to: `/app/reports/surepass-itr-flow/${itrCreateSvc.id}`,
                        serviceId: itrCreateSvc.id,
                    });
                }

                if (hasSeFlow) {
                    serviceItems.unshift({
                        label: 'Shops & Establishment Details',
                        icon: 'pi pi-fw pi-shop',
                        to: `/app/reports/surepass-se-flow/${seStateListSvc.id}/${seDetailsSvc.id}`,
                        serviceId: seDetailsSvc.id,
                    });
                }

                if (hasFuelFlow) {
                    serviceItems.unshift({
                        label: 'Fuel Price',
                        icon: 'pi pi-fw pi-car',
                        to: `/app/reports/surepass-fuel-price-flow/${fuelStateListSvc.id}/${fuelPriceSvc.id}`,
                        serviceId: fuelPriceSvc.id,
                    });
                }

                if (hasElectricityFlow) {
                    serviceItems.unshift({
                        label: 'Electricity Bill',
                        icon: 'pi pi-fw pi-bolt',
                        to: `/app/reports/surepass-electricity-flow/${electricityOperatorListSvc.id}/${electricityBillSvc.id}`,
                        serviceId: electricityBillSvc.id,
                    });
                }

                return {
                    label: category.name,
                    icon: getSimpleIcon(category.name),
                    items: serviceItems,
                };
            });
    }
    
    // Move GSTIN Advanced from Corporate (or any non-Financial category) into Financial Reports
    const financialCat = categoryItems.find((c) => (c.label || '').toLowerCase().includes('financial'));
    if (financialCat) {
        for (let i = categoryItems.length - 1; i >= 0; i--) {
            const cat = categoryItems[i];
            if ((cat.label || '').toLowerCase().includes('financial')) continue;
            const idx = (cat.items || []).findIndex(
                (item) => (item.label || '').toLowerCase().includes('gstin') && (item.label || '').toLowerCase().includes('advanced')
            );
            if (idx !== -1) {
                const [moved] = cat.items.splice(idx, 1);
                const itrIdx = financialCat.items.findIndex((item) => (item.label || '').toLowerCase().includes('itr'));
                if (itrIdx !== -1) {
                    financialCat.items.splice(itrIdx, 0, moved);
                } else {
                    financialCat.items.push(moved);
                }
                if (cat.items.length === 0) categoryItems.splice(i, 1);
                break;
            }
        }
    }

    // Add Report History at the end
    categoryItems.push({
        label: 'Report History',
        icon: 'pi pi-fw pi-history',
        to: '/app/report-history'
    });
    
    return {
        label: 'Reports',
        icon: 'pi pi-fw pi-file',
        items: categoryItems
    };
};

// Map known navigation_url values (stored in DB) to the correct React route paths in App.js
const resolveKnownNavUrl = (navUrl, serviceId) => {
    const u = navUrl.toLowerCase().replace(/_/g, '-');

    // PAN
    if (u.includes('/tartan/pan') && (u.includes('v2') || u.includes('-v2'))) return `/app/reports/pan-details-v2/${serviceId}`;
    if (u.includes('/tartan/pan')) return `/app/reports/pan-details/${serviceId}`;

    // Voter ID
    if ((u.includes('/tartan/voter') || u.includes('/tartan/voterid')) && (u.includes('v2') || u.includes('-v2'))) return `/app/reports/voter-id-details-v2/${serviceId}`;
    if (u.includes('/tartan/voter')) return `/app/reports/voter-id-validation/${serviceId}`;

    // Driving Licence
    if ((u.includes('/tartan/driving') || u.includes('/tartan/licence') || u.includes('/tartan/license')) && (u.includes('v2') || u.includes('-v2'))) return `/app/reports/driving-licence-v2/${serviceId}`;
    if (u.includes('/tartan/driving') || u.includes('/tartan/licence') || u.includes('/tartan/license')) return `/app/reports/driving-licence-detailed/${serviceId}`;

    // RC Details
    if (u.includes('/tartan/rc')) return `/app/reports/rc-details/${serviceId}`;

    // CA Membership
    if (u.includes('/tartan/ca')) return `/app/reports/ca-membership-verification/${serviceId}`;

    // Doctor / Nurse Verification
    if (u.includes('/tartan/doctor')) return `/app/reports/doctor-registration-verification/${serviceId}`;
    if (u.includes('/tartan/nurse')) return `/app/reports/nurse-registration-verification/${serviceId}`;

    // Mobile Bureau
    if (u.includes('/tartan/mobile')) return `/app/reports/mobile-bureau-lookup/${serviceId}`;

    // Employment
    if (u.includes('/tartan/uan') || u.includes('/tartan/epfo') || (u.includes('/tartan/employment') && u.includes('uan'))) return `/app/reports/uanNumberToEmploymentHistoryWithDoE/${serviceId}`;
    if (u.includes('/tartan/employment')) return `/app/reports/employment-history-from-mobile-comprehensive/${serviceId}`;

    // Equifax / Experian
    if (u.includes('/equifax')) return `/app/reports/equifax/${serviceId}`;
    if (u.includes('/experian')) return `/app/reports/experian-report/${serviceId}`;

    // GLIB (Autosift) services.
    //
    // NOTE: this resolver is fed the DB **navigation_url**, not the
    // controller_url — so match on the nav-url shape
    // (`/app/reports/glib-gst-return-analysis`), not on the API path.
    // Getting this wrong sent GST to the BSA page: `/glib/gst` never matched,
    // while the catch-all `/glib` did.
    //
    // Order matters twice over:
    //   • the specific GLIB service before the catch-all `/glib`
    //   • all GLIB rules before the BoostMoney rule below, whose
    //     `/bank-statement` substring also matches GLIB's BSA nav url.
    // Fetch (consent journey) must be tested BEFORE the generic `glib-gst`
    // rule, which would otherwise send it to the upload/analysis page.
    if (u.includes('glib-itr-fetch') || u.includes('itr-fetch')) return `/app/reports/glib-itr-fetch/${serviceId}`;
    if (u.includes('glib-gst-return-fetch') || u.includes('gst-fetch')) return `/app/reports/glib-gst-return-fetch/${serviceId}`;
    if (u.includes('glib-gst')) return `/app/reports/glib-gst-return-analysis/${serviceId}`;
    if (u.includes('glib-bank-statement')) return `/app/reports/glib-bank-statement-analysis/${serviceId}`;
    // Fallbacks in case a service is registered with the API path as its
    // navigation_url instead of the /app/reports/... form.
    if (u.includes('/glib/gst')) return `/app/reports/glib-gst-return-analysis/${serviceId}`;
    if (u.includes('/glib')) return `/app/reports/glib-bank-statement-analysis/${serviceId}`;

    // Bank Statement (BoostMoney)
    if (u.includes('/boostmoney') || u.includes('/bank-statement')) return `/app/reports/bank-statement-analysis/${serviceId}`;

    // GSTIN Advanced (Surepass)
    if (u.includes('/surepass/gstin') || u.includes('gstin-advanced')) return `/app/reports/gstin-advanced/${serviceId}`;

    // Surepass TAN APIs (order matters: specific paths before generic)
    if (u.includes('/surepass/tan') && u.includes('tds-check')) return `/app/reports/surepass-tan-tds-check/${serviceId}`;
    if (u.includes('/surepass/tan') && u.includes('company-search')) return `/app/reports/surepass-tan-company-search/${serviceId}`;
    if (u.includes('/surepass/tan') && u.includes('tan-advanced')) return `/app/reports/surepass-tan-advanced/${serviceId}`;
    if (u.includes('/surepass/tan')) return `/app/reports/surepass-tan-verify/${serviceId}`;

    // Surepass TIN
    if (u.includes('/surepass/tin')) return `/app/reports/surepass-tin-verification/${serviceId}`;

    // Surepass IRDAI
    if (u.includes('/surepass/irdai')) return `/app/reports/surepass-irdai-verify/${serviceId}`;

    // Surepass Death Certificate
    if (u.includes('/surepass/death')) return `/app/reports/surepass-death-verification/${serviceId}`;

    // Surepass Prefill
    if (u.includes('/surepass/prefill') && u.includes('by-mobile')) return `/app/reports/surepass-prefill-by-mobile/${serviceId}`;
    if (u.includes('/surepass/prefill')) return `/app/reports/surepass-prefill-report/${serviceId}`;

    // Surepass Employment Email
    if (u.includes('/surepass/employment') && u.includes('email')) return `/app/reports/surepass-employment-email-check/${serviceId}`;

    // Surepass SMS
    if (u.includes('/surepass/sms') && u.includes('submit')) return `/app/reports/surepass-sms-submit-otp/${serviceId}`;
    if (u.includes('/surepass/sms')) return `/app/reports/surepass-sms-send-otp/${serviceId}`;

    // Surepass Digilocker
    if (u.includes('/surepass/digilocker') && u.includes('download')) return `/app/reports/surepass-digilocker-download-document/${serviceId}`;
    if (u.includes('/surepass/digilocker') && u.includes('list')) return `/app/reports/surepass-digilocker-list-documents/${serviceId}`;
    if (u.includes('/surepass/digilocker') && u.includes('initialize')) return `/app/reports/surepass-digilocker-initialize/${serviceId}`;
    if (u.includes('/surepass/digilocker') && u.includes('status')) return `/app/reports/surepass-digilocker-status/${serviceId}`;

    // Surepass GSTIN
    if (u.includes('/surepass/gstin-by-pan')) return `/app/reports/surepass-gstin-by-pan/${serviceId}`;
    if (u.includes('/surepass/gstin')) return `/app/reports/surepass-gstin/${serviceId}`;

    // Surepass Company/RERA/MCA
    if (u.includes('/surepass/company-details')) return `/app/reports/surepass-company-details/${serviceId}`;
    if (u.includes('/surepass/rera')) return `/app/reports/surepass-rera-v2/${serviceId}`;
    if (u.includes('/surepass/mca') && u.includes('order-list')) return `/app/reports/surepass-mca-order-list/${serviceId}`;
    if (u.includes('/surepass/mca') && u.includes('create-order')) return `/app/reports/surepass-mca-create-order/${serviceId}`;

    // Surepass Udyam/Udyog
    if (u.includes('/surepass/udyog-aadhar')) return `/app/reports/surepass-udyog-aadhar/${serviceId}`;
    if (u.includes('/surepass/udyam-otp') && u.includes('submit')) return `/app/reports/surepass-udyam-otp-submit/${serviceId}`;
    if (u.includes('/surepass/udyam-otp') && u.includes('send')) return `/app/reports/surepass-udyam-otp-send/${serviceId}`;

    // Surepass CIBIL
    if (u.includes('/surepass/credit-report-cibil') && u.includes('pdf')) return `/app/reports/surepass-cibil-report-pdf/${serviceId}`;
    if (u.includes('/surepass/credit-report-cibil')) return `/app/reports/surepass-cibil-report/${serviceId}`;

    // Surepass ITR
    if (u.includes('/surepass/itr') && u.includes('create-client')) return `/app/reports/surepass-itr-create-client/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('check-credentials')) return `/app/reports/surepass-itr-check-credentials/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('download-profile')) return `/app/reports/surepass-itr-download-profile/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('download-itr-v')) return `/app/reports/surepass-itr-download-itr-v/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('download-26as')) return `/app/reports/surepass-itr-download-26as/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('download-ais-v2')) return `/app/reports/surepass-itr-download-ais-v2/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('download-itr')) return `/app/reports/surepass-itr-download-itr/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('forget-password')) return `/app/reports/surepass-itr-forget-password/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('submit-otp')) return `/app/reports/surepass-itr-submit-otp/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('get-ais')) return `/app/reports/surepass-itr-get-ais/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('get-profile')) return `/app/reports/surepass-itr-get-profile/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('get-itr-details')) return `/app/reports/surepass-itr-get-itr-details/${serviceId}`;
    if (u.includes('/surepass/itr') && u.includes('get-26as-details')) return `/app/reports/surepass-itr-get-26as-details/${serviceId}`;

    // Surepass Face
    if (u.includes('/surepass/face') && u.includes('match')) return `/app/reports/surepass-face-match/${serviceId}`;
    if (u.includes('/surepass/face') && u.includes('liveness-v3')) return `/app/reports/surepass-face-liveness-v3/${serviceId}`;
    if (u.includes('/surepass/face') && u.includes('liveness-v4')) return `/app/reports/surepass-face-liveness-v4/${serviceId}`;
    if (u.includes('/surepass/face') && u.includes('liveness')) return `/app/reports/surepass-face-liveness/${serviceId}`;
    if (u.includes('/surepass/face') && u.includes('background-remover')) return `/app/reports/surepass-face-background-remover/${serviceId}`;
    if (u.includes('/surepass/face') && u.includes('extract')) return `/app/reports/surepass-face-extract/${serviceId}`;

    // Surepass Utility/SE
    if (u.includes('/surepass/utility') && u.includes('electricity')) return `/app/reports/surepass-electricity-operator-list/${serviceId}`;
    if (u.includes('/surepass/se') && u.includes('state-code-list')) return `/app/reports/surepass-se-state-list/${serviceId}`;
    if (u.includes('/surepass/se') && u.includes('details')) return `/app/reports/surepass-se-details/${serviceId}`;

    // Surepass Esign
    if (u.includes('/surepass/esign') && u.includes('initialize-nsdl-stamp')) return `/app/reports/surepass-esign-initialize-nsdl-stamp/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('initialize-suresign')) return `/app/reports/surepass-esign-initialize-suresign/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('initialize-hsm')) return `/app/reports/surepass-esign-initialize-hsm/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('initialize')) return `/app/reports/surepass-esign-initialize/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('get-upload-link')) return `/app/reports/surepass-esign-get-upload-link/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('set-branding')) return `/app/reports/surepass-esign-set-branding/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('upload-pdf-via-link')) return `/app/reports/surepass-esign-upload-pdf-via-link/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('get-signed-document')) return `/app/reports/surepass-esign-get-signed-document/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('get-audit-trail')) return `/app/reports/surepass-esign-get-audit-trail/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('get-status')) return `/app/reports/surepass-esign-get-status/${serviceId}`;
    if (u.includes('/surepass/esign') && u.includes('get-report')) return `/app/reports/surepass-esign-get-report/${serviceId}`;

    // Surepass Utility extras
    if (u.includes('/surepass/utilities/fuel-price') && u.includes('state-list')) return `/app/reports/surepass-fuel-price-state-list/${serviceId}`;
    if (u.includes('/surepass/utilities/fuel-price')) return `/app/reports/surepass-fuel-price/${serviceId}`;
    if (u.includes('/surepass/utilities/imei')) return `/app/reports/surepass-imei-verification/${serviceId}`;
    if (u.includes('/surepass/utilities/domain')) return `/app/reports/surepass-domain-verification/${serviceId}`;
    if (u.includes('/surepass/utilities/mobile-age')) return `/app/reports/surepass-mobile-age/${serviceId}`;
    if (u.includes('/surepass/utility/electricity') && u.includes('bill')) return `/app/reports/surepass-electricity-bill/${serviceId}`;
    if (u.includes('/surepass/mca') && u.includes('get-status')) return `/app/reports/surepass-mca-get-status/${serviceId}`;

    return null;
};

// Simple route builder with service ID
const buildSimpleRoute = (service) => {
    if (service.navigation_url) {
        // First try to resolve to a known working route
        const resolved = resolveKnownNavUrl(service.navigation_url, service.id);
        if (resolved) return resolved;

        let url = service.navigation_url;

        // Ensure URL starts with /app
        if (!url.startsWith('/')) {
            url = '/' + url;
        }
        if (!url.startsWith('/app')) {
            if (url.startsWith('/')) {
                url = '/app' + url;
            } else {
                url = '/app/' + url;
            }
        }

        // Append service ID to the URL
        if (!url.includes(service.id)) {
            url = `${url}/${service.id}`;
        }

        return url;
    }

    const slug = service.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `/app/reports/${slug}/${service.id}`;
};

// Map service names to feature keys for profile completion check
const getFeatureKeyFromService = (serviceName) => {
    const name = serviceName.toLowerCase();
    
    // Credit Bureau Reports - require profile completion
    if (name.includes('equifax')) return 'equifax';
    if (name.includes('experian')) return 'experian';
    
    // Identity and Address Verification - require profile completion
    if (name.includes('pan')) return 'pan-verification';
    if (name.includes('aadhaar') || name.includes('aadhar')) return 'aadhaar-verification';
    if (name.includes('voter')) return 'voter-id';
    if (name.includes('driving') || name.includes('licence') || name.includes('license')) return 'driving-licence';
    if (name.includes('passport')) return 'passport-verification';
    if (name.includes('gstin') || name.includes('gst')) return 'gstin-verification';
    if (name.includes('tan') && !name.includes('bank')) return 'tan-verification';
    if (name.includes('tin')) return 'tin-verification';
    if (name.includes('irdai')) return 'irdai-verification';
    if (name.includes('death')) return 'death-verification';
    if (name.includes('prefill')) return 'prefill-report';
    if (name.includes('digilocker')) return 'digilocker';
    if (name.includes('sms') && name.includes('otp')) return 'sms-otp';
    if (name.includes('udyam') || name.includes('udyog')) return 'udyam-verification';
    if (name.includes('fssai')) return 'fssai-verification';
    if (name.includes('shop') && name.includes('establishment')) return 'shop-establishment-verification';
    if (name.includes('trademark')) return 'trademark-verification';
    
    // Financial Verification - require profile completion
    if (name.includes('bank') && name.includes('statement')) return 'bank-statement';
    if (name.includes('mobile') && name.includes('bureau')) return 'mobile-bureau';
    
    // Employment Verification - require profile completion
    if (name.includes('employment')) return 'employment-history';
    if (name.includes('uan') || name.includes('epfo') || name.includes('pf')) return 'employment-uan';
    if (name.includes('esi') || name.includes('esic')) return 'employment-esi';
    
    // Professional Verification - require profile completion
    if (name.includes('ca') || name.includes('membership')) return 'ca-membership';
    if (name.includes('doctor') || name.includes('medical')) return 'doctor-verification';
    if (name.includes('advocate') || name.includes('lawyer')) return 'advocate-verification';
    
    if (name.includes('cibil')) return 'cibil';
    if (name.includes('itr') || name.includes('income tax')) return 'itr';
    if (name.includes('face') || name.includes('liveness')) return 'face';
    if (name.includes('esign') || name.includes('e-sign')) return 'esign';
    if (name.includes('rera')) return 'rera';
    if (name.includes('mca')) return 'mca';
    if (name.includes('company')) return 'company';
    if (name.includes('electricity') || name.includes('utility') || name.includes('se state') || name.includes('se detail')) return 'utility';
    if (name.includes('fuel price')) return 'utility';
    if (name.includes('imei')) return 'utility';
    if (name.includes('domain')) return 'utility';
    if (name.includes('mobile age')) return 'utility';

    // Default to blocking access for unrecognized services (better safe than sorry)
    return 'unknown-service';
};

// Simple icon mapping
const getSimpleIcon = (name) => {
    // Add null/undefined check
    if (!name || typeof name !== 'string') {
        return 'pi pi-fw pi-file'; // Default icon
    }

    const serviceName = name.toLowerCase();
    
    // Category icons
    if (serviceName.includes('corporation') || serviceName.includes('corporate')) return 'pi pi-fw pi-building';
    if (serviceName.includes('identity') || serviceName.includes('verification')) return 'pi pi-fw pi-user-edit';
    if (serviceName.includes('financial') || serviceName.includes('credit')) return 'pi pi-fw pi-chart-line';
    if (serviceName.includes('employment')) return 'pi pi-fw pi-briefcase';
    if (serviceName.includes('vehicle')) return 'pi pi-fw pi-car';
    if (serviceName.includes('professional')) return 'pi pi-fw pi-verified';

    // Service-specific icons
    if (serviceName.includes('gstin') || serviceName.includes('gst')) return 'pi pi-fw pi-building';
    if (serviceName.includes('tan') && !serviceName.includes('bank')) return 'pi pi-fw pi-id-card';
    if (serviceName.includes('tin')) return 'pi pi-fw pi-hashtag';
    if (serviceName.includes('irdai')) return 'pi pi-fw pi-shield';
    if (serviceName.includes('death')) return 'pi pi-fw pi-file-o';
    if (serviceName.includes('prefill')) return 'pi pi-fw pi-user';
    if (serviceName.includes('digilocker')) return 'pi pi-fw pi-folder';
    if (serviceName.includes('sms') || serviceName.includes('otp')) return 'pi pi-fw pi-send';
    if (serviceName.includes('email') && serviceName.includes('check')) return 'pi pi-fw pi-envelope';
    if (serviceName.includes('equifax')) return 'pi pi-fw pi-chart-line';
    if (serviceName.includes('bank')) return 'pi pi-fw pi-building';
    if (serviceName.includes('pan')) return 'pi pi-fw pi-credit-card';
    if (serviceName.includes('voter')) return 'pi pi-fw pi-check-square';
    if (serviceName.includes('driving')) return 'pi pi-fw pi-car';
    if (serviceName.includes('mobile')) return 'pi pi-fw pi-phone';
    if (serviceName.includes('ca') || serviceName.includes('membership')) return 'pi pi-fw pi-verified';
    if (serviceName.includes('cibil')) return 'pi pi-fw pi-chart-bar';
    if (serviceName.includes('itr') || serviceName.includes('income tax')) return 'pi pi-fw pi-file-pdf';
    if (serviceName.includes('face') || serviceName.includes('liveness')) return 'pi pi-fw pi-camera';
    if (serviceName.includes('esign') || serviceName.includes('e-sign')) return 'pi pi-fw pi-file';
    if (serviceName.includes('rera')) return 'pi pi-fw pi-home';
    if (serviceName.includes('mca')) return 'pi pi-fw pi-briefcase';
    if (serviceName.includes('company')) return 'pi pi-fw pi-building';
    if (serviceName.includes('udyam') || serviceName.includes('udyog')) return 'pi pi-fw pi-briefcase';
    if (serviceName.includes('electricity') || serviceName.includes('utility')) return 'pi pi-fw pi-bolt';
    if (serviceName.includes('se state') || serviceName.includes('se detail')) return 'pi pi-fw pi-map';
    if (serviceName.includes('fuel')) return 'pi pi-fw pi-chart-bar';
    if (serviceName.includes('imei')) return 'pi pi-fw pi-mobile';
    if (serviceName.includes('domain')) return 'pi pi-fw pi-globe';
    if (serviceName.includes('mobile age')) return 'pi pi-fw pi-phone';
    if (serviceName.includes('digilocker')) return 'pi pi-fw pi-folder';
    if (serviceName.includes('prefill')) return 'pi pi-fw pi-user';
    if (serviceName.includes('tax')) return 'pi pi-fw pi-money-bill';
    if (serviceName.includes('corporate')) return 'pi pi-fw pi-building';
    if (serviceName.includes('e-signature')) return 'pi pi-fw pi-file-edit';

    return 'pi pi-fw pi-file';
};

export default menuService;